/**
 * BTW-weergave voor renovatiefacturen.
 *
 * Odoo boekt BTW op aparte boekingsregels, waardoor de kostenregel van een
 * factuur altijd EXCL btw staat. De backend levert daarom per factuur:
 *
 *   amount_excl_vat / amount_vat / amount_incl_vat / vat_percentage / vat_source
 *
 * `vat_source` zegt hoe betrouwbaar die cijfers zijn:
 *  - "odoo_tax"      een btw-code staat ingesteld; percentage en bedrag komen
 *                    uit Odoo. Let op: 6% medecontractant geeft terecht EUR 0 btw.
 *  - "none"          geen btw-code — de btw is ONBEKEND, niet nul.
 *  - "import_failed" de Peppol-import kon de btw-code niet koppelen, dus het
 *                    bedrag dat als "excl btw" geboekt staat is in werkelijkheid
 *                    het brutobedrag. Moet zichtbaar gemarkeerd worden.
 *
 * Deze module bevat alleen pure helpers, zodat elke tabel (importmodal,
 * documentenlijst, renovatietab) dezelfde regels gebruikt.
 */

export type VatSource = "odoo_tax" | "none" | "import_failed";

/** Wat de backend-feeds per te importeren factuur meesturen. */
export interface InvoiceVatFields {
  amount_excl_vat?: number | null;
  amount_vat?: number | null;
  amount_incl_vat?: number | null;
  vat_percentage?: number | null;
  vat_label?: string | null;
  vat_source?: VatSource | string | null;
  /** Ouder veld, nog steeds excl btw. Dient als fallback. */
  amount?: number | null;
}

/** Wat op een DocumentLine is opgeslagen. */
export interface DocumentLineVatFields {
  totalExclVat?: number | string | null;
  vatPercentage?: number | string | null;
  vatAmount?: number | string | null;
  totalInclVat?: number | string | null;
  vatSource?: VatSource | string | null;
  /** Gezet op regels die uit Odoo geïmporteerd zijn (de boekingsregel-id). */
  odooId?: string | number | null;
}

/**
 * Of het opgeslagen btw-percentage van een regel te vertrouwen is.
 *
 * Facturen die geïmporteerd zijn vóór de btw-uitsplitsing bestond, hebben
 * vatPercentage 0 opgeslagen omdat de frontend dat hardcoded meestuurde. Dat is
 * geen gemeten 0%-tarief maar een ontbrekende meting, en die mag niet als "0% btw"
 * getoond worden. Zulke regels zijn te herkennen aan een odooId ZONDER vatSource.
 *
 * Handmatig ingevoerde regels (geen odooId) hebben wél een door de gebruiker
 * ingetypt tarief; dat blijft gewoon geldig.
 */
const hasTrustworthyPercentage = (line: DocumentLineVatFields): boolean => {
  if (line.vatPercentage === null || line.vatPercentage === undefined) return false;
  if (line.vatSource) return true;
  return !line.odooId;
};

export const VAT_UNKNOWN = "—";

/** Tekst bij een btw die niet uit Odoo te halen is. Null = niets tonen. */
export const VAT_WARNING_TEXT = "BTW ontbreekt in Odoo";

const toNumber = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export interface VatDisplay {
  /** Altijd positieve weergavebedragen. */
  exclVat: number;
  vatAmount: number;
  inclVat: number;
  /** "6%", "0% M" of "—" wanneer er geen btw-code is. */
  percentageLabel: string;
  /** Volledige naam van de btw-code uit Odoo, voor een tooltip. */
  percentageTitle?: string;
  /** True wanneer de btw niet uit Odoo te bepalen is. */
  isUnknown: boolean;
  /** Gezet wanneer de cijfers de factuur tegenspreken; anders null. */
  warning: string | null;
}

/**
 * Percentage netjes tonen: hele getallen zonder decimalen (6%), halve wel (6,5%).
 */
export const formatVatPercentage = (percentage: number): string => {
  const rounded = Math.round(percentage * 100) / 100;
  return Number.isInteger(rounded)
    ? `${rounded}%`
    : `${rounded.toLocaleString("nl-BE", { maximumFractionDigits: 2 })}%`;
};

const buildDisplay = (params: {
  exclVat: number;
  vatAmount: number;
  inclVat: number;
  percentage: number | null | undefined;
  label: string | null | undefined;
  source: string | null | undefined;
}): VatDisplay => {
  const { exclVat, vatAmount, inclVat, percentage, label, source } = params;

  // Geen btw-code betekent "onbekend", niet "0%". 0% expliciet in Odoo
  // (bijvoorbeeld "0% Investeringsgoederen") is wél een echte 0%.
  const hasPercentage = percentage !== null && percentage !== undefined;

  return {
    exclVat: Math.abs(exclVat),
    vatAmount: Math.abs(vatAmount),
    inclVat: Math.abs(inclVat),
    percentageLabel: hasPercentage ? formatVatPercentage(percentage) : VAT_UNKNOWN,
    percentageTitle: label ?? undefined,
    isUnknown: !hasPercentage,
    warning: source === "import_failed" ? VAT_WARNING_TEXT : null,
  };
};

/**
 * BTW-weergave voor een nog te importeren factuur uit een backend-feed.
 */
export const resolveInvoiceVat = (invoice: InvoiceVatFields): VatDisplay => {
  // Valt terug op `amount` (excl btw) voor feeds/tests van vóór de btw-velden.
  const exclVat = toNumber(invoice.amount_excl_vat ?? invoice.amount);
  const vatAmount = toNumber(invoice.amount_vat);

  return buildDisplay({
    exclVat,
    vatAmount,
    inclVat: toNumber(invoice.amount_incl_vat) || Math.abs(exclVat) + Math.abs(vatAmount),
    percentage: invoice.vat_percentage,
    label: invoice.vat_label,
    source: invoice.vat_source,
  });
};

/**
 * BTW-weergave voor een reeds geïmporteerd document: telt de regels op.
 *
 * Het percentage is alleen zinvol als alle regels hetzelfde tarief hebben — bij
 * een gesplitste factuur met verschillende tarieven wordt "gemengd" getoond in
 * plaats van een misleidend gemiddelde.
 */
export const resolveDocumentVat = (
  lines: DocumentLineVatFields[] | undefined | null,
): VatDisplay => {
  const rows = lines ?? [];

  const exclVat = rows.reduce(
    (sum, line) => sum + Math.abs(toNumber(line.totalExclVat)),
    0,
  );
  const vatAmount = rows.reduce(
    (sum, line) => sum + Math.abs(toNumber(line.vatAmount)),
    0,
  );
  // totalInclVat is nullable in het schema; val terug op excl + btw.
  const inclVat = rows.reduce(
    (sum, line) =>
      sum +
      Math.abs(
        line.totalInclVat !== null && line.totalInclVat !== undefined
          ? toNumber(line.totalInclVat)
          : toNumber(line.totalExclVat) + toNumber(line.vatAmount),
      ),
    0,
  );

  const percentages = Array.from(
    new Set(
      rows
        .filter(hasTrustworthyPercentage)
        .map((line) => toNumber(line.vatPercentage)),
    ),
  );

  const sources = new Set(rows.map((line) => line.vatSource).filter(Boolean));

  const base = buildDisplay({
    exclVat,
    vatAmount,
    inclVat,
    percentage: percentages.length === 1 ? percentages[0] : null,
    label: null,
    source: sources.has("import_failed") ? "import_failed" : undefined,
  });

  if (percentages.length > 1) {
    return { ...base, percentageLabel: "gemengd", isUnknown: false };
  }

  return base;
};
